import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
//import amplitude from 'amplitude-js';

import { toastActions } from '$store/toast';
import { balanceActions, balanceSelector } from '$store/balance/index';
import { Api } from '$api';
import { batchActions } from '$store';
import { contentPrioritySelector, currentUserSelector, mainActions } from '$store/main';
import {
  GenerateInvoiceAction,
  TopUpAction,
  GetAddressAction,
  PayAction,
  WithdrawAction,
  TopUpFromTonBalanceAction,
} from '$store/balance/interface';
import router from '$navigation/router';
import { CHAT } from '$shared/constants/pages';
import { payActions } from '$store/pay';
import { balancePayActions, balancePaySelector } from '$store/balancePay';
import { BalancePayAction, BalancePayState } from '$store/balancePay/interface';
import { CurrencyEnum } from '$store/models';
import { imActions, imAsPeerIdSelector, imHistorySelector } from '$store/im';
import { t } from '$translation';
import { goBack, goTo } from '$navigation/helper';
import { ContentPriorityType } from '$store/main/interface';
import { CryptoPayTopUpAction, PayProvider } from '$store/pay/interface';
import { getStorageItem, statReachGoal, trackPurchase } from '$utils';
import { rouletteActions } from '$store/roulette';
import { bundlesActions } from '$store/bundles';
import { adventCalendarActions } from '$store/adventCalendar';
import { OnlypaySheet } from '$core';

function* loadBalanceWorker() {
  try {
    const { params } = yield call(router.getState);
    let currency = params.currency;
    if (!currency) {
      const user = yield select(currentUserSelector);
      const contentPriority = yield select(contentPrioritySelector);
      if (contentPriority === ContentPriorityType.ADULT || user.verified === 2) {
        currency = CurrencyEnum.EUR;
      } else {
        currency = CurrencyEnum.TON;
      }
    }

    const data = yield call(Api.get, '/balance', {
      params: {
        currency,
      },
    });

    yield put(
      batchActions(
        balanceActions.setLoaded({
          balance: data.balance,
          eurBalance: data.eurBalance,
          operations: data.operations,
          withdrawals: data.withdrawals,
          isModel: data.isModel,
          bscAddress: data.bscAddress,
          likesCount: data.likesCount,
          likePrice: data.likePrice,
        }),
        balanceActions.setIsGold(data.isGold),
        mainActions.setGroups(data.groups),
        mainActions.setBalance(data.balance),
        mainActions.setEurBalance(data.eurBalance),
        mainActions.setPlaceBalance(data.placeBalance),
        balanceActions.setHoldBalance(data.eurHoldBalance),
        balanceActions.setTonHoldBalance(data.tonHoldBalance),
        balanceActions.setHoldDays(data.holdDays || 21),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(balanceActions.setFailed());
  }
}

function* generateInvoiceWorker(action: GenerateInvoiceAction) {
  const { authorId, categoryId } = action.payload;
  try {
    yield put(toastActions.setToastLoading());

    let url = '/balance/create_invoice/';
    if (authorId) {
      url = `/balance/create_invoice/author/${authorId}`;
    } else if (categoryId) {
      url = `/balance/create_invoice/category/${categoryId}`;
    }

    const data = yield call(Api.get, url);
    yield put(
      balanceActions.setInvoiceUrl({
        invoiceUrl: data.invoiceUrl,
      }),
    );
    action.payload.onDone(data.invoiceUrl);
    yield put(toastActions.hideToast());
  } catch (e) {
    yield put(batchActions(toastActions.hideToast()));
    yield put(toastActions.setToastFail(e.message));
  }
}

function* withdrawWorker(action: WithdrawAction) {
  try {
    yield put(toastActions.setToastLoading());

    const { amount, address, agentId, comment } = action.payload;
    yield call(Api.post, '/balance/withdraw', {
      amount,
      address,
      agentId,
      comment,
    });
    yield put(toastActions.setToastSuccess());
    action.payload.onDone();
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* getAddressWorker(action: GetAddressAction) {
  try {
    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/balance/address', {
      network: action.payload,
    });
    yield put(toastActions.setToastSuccess());
    yield put(balanceActions.setBscAddress(data));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

export function actionToTarget(action: BalancePayAction) {
  let target: string | undefined = undefined;
  if (action === BalancePayAction.MESSAGE || action === BalancePayAction.TIPS) {
    target = 'chat';
  } else if (action === BalancePayAction.SUBSCRIPTION_MEDIA) {
    target = 'media_subscription';
  } else if (action === BalancePayAction.SUBSCRIPTION_PERSONAL) {
    target = 'sponsor_subscription';
  } else if (action === BalancePayAction.SUBSCRIPTION_ADULT) {
    target = 'adult_subscription';
  } else if (action === BalancePayAction.BATTLE_DONATE) {
    target = 'battle';
  } else if (action === BalancePayAction.BUNDLE) {
    target = 'bundle';
  }
  return target;
}

export function actionToTriggerType(action: BalancePayAction) {
  if (action === BalancePayAction.MESSAGE) {
    return 'message';
  } else if (action === BalancePayAction.TIPS) {
    return 'tips';
  } else if (action === BalancePayAction.BUNDLE) {
    return 'bundle';
  } else if (action === BalancePayAction.SUBSCRIPTION_PERSONAL) {
    return 'authorSubscription';
  }
  return '';
}

function* cryptoBayTopUpWorker({ payload }: CryptoPayTopUpAction) {
  const { amount, currency } = payload;
  const { action, objectId, extraId } = yield select(balancePaySelector);
  try {
    yield put(toastActions.setToastLoading());
    const { payUrl } = yield call(Api.post, '/cryptopay/top_up', {
      amount,
      currency,
      target: actionToTarget(action),
      targetObject: +objectId,
      extraId: +extraId,
      triggerPay: actionToTriggerType(action),
      triggerPayId: +objectId,
    });
    yield put(balanceActions.setCryptoPayUrl(payUrl));
    yield updateAllBalancesWorker();
    yield put(toastActions.hideToast());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* topUpWorker({ payload }: TopUpAction) {
  const { amount, skipAutoPay, cardId = 0, provider, onUrl } = payload;
  const { action, objectId, extraId } = yield select(balancePaySelector);
  try {
    yield put(
      batchActions(
        payActions.setIsFailed(false),
        payActions.setErrorCode(null),
        toastActions.setToastLoading(),
      ),
    );
    const randomId = Date.now();
    const { token, status, url } = yield call(Api.post, '/pay/top_up', {
      amount,
      skipAutoPay,
      target: actionToTarget(action),
      targetObject: +objectId,
      extraId: +extraId,
      randomId: randomId,
      skipRedirect: true,
      cardId,
      provider,
      triggerPay: actionToTriggerType(action),
      triggerPayId: +objectId,
    });

    yield put(payActions.setRandomInvoiceId(randomId));

    if (status === 'DECLINE' || status === 'FAILED') {
      yield put(toastActions.setToastFail());
    } else if (url && onUrl) {
      yield put(toastActions.hideToast());
      onUrl(url);
    } else if (token) {
      yield put(payActions.setInvoiceToken(token));
      yield put(toastActions.hideToast());
    } else {
      yield put(payActions.setIsWaitingForPay(true));

      const { timeout } = yield race({
        resolved: take([payActions.setIsWaitingForPay.type]),
        timeout: delay(30_000),
      });

      if (timeout) {
        yield put(payActions.setIsWaitingForPay(false));
        yield put(toastActions.setToastFail());
      }
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    // yield call(router.navigate, TOP_UP_FAILED);
  }
}

function* topUpRetryWorker() {
  const { topUp } = yield select(balanceSelector);
  yield put(
    batchActions(
      payActions.setIsFailed(false),
      payActions.setErrorCode(null),
      payActions.setInvoiceToken(null), // Need to rerender iframe
      balanceActions.topUp(topUp),
    ),
  );
}

function* payWorker(a: PayAction) {
  try {
    yield put(
      batchActions(
        payActions.setIsFailed(false),
        payActions.setErrorCode(null),
        toastActions.setToastLoading(),
      ),
    );
    const {
      action,
      objectId,
      extraId,
      amount,
      isAnon = false,
      period = 0,
      closeSheet = true,
      hideSuccessToast = false,
    } = a.payload;
    let method: string;
    const params: any = {
      currency: CurrencyEnum.EUR,
    };
    if (action === BalancePayAction.MESSAGE) {
      method = `/im/${extraId}/buy`;
    } else if (action === BalancePayAction.TIPS) {
      method = `/im/tips/${extraId}/send`;
    } else if (action === BalancePayAction.SUBSCRIPTION_ADULT) {
      method = '/balance/subscription/buy';
      params.categoryId = 1;
      params.period = period;

      const targetId = getStorageItem('targetId');
      params.targetId = targetId ? parseInt(targetId, 10) || 0 : 0;
    } else if (action === BalancePayAction.SUBSCRIPTION_MEDIA) {
      method = '/balance/subscription/buy';
      params.categoryId = 2;
    } else if (action === BalancePayAction.SUBSCRIPTION_PERSONAL) {
      method = `/balance/subscription/buy`;
      params.authorId = objectId;
      params.categoryId = 3;
      params.targetId = objectId;
    } else if (action === BalancePayAction.BATTLE_DONATE) {
      method = `/battle/${objectId}/donate`;
      params.anonymous = isAnon;
      params.amount = amount;
      params.userId = extraId;
    } else if (action === BalancePayAction.ROULETTE) {
      method = `/roulette/rotate`;
      params.rouletteId = objectId;
      statReachGoal('roulette_confirm_rotate', {
        rouletteId: objectId,
      });
    } else if (action === BalancePayAction.VALENTINE) {
      method = `/im/${objectId}/valentine_day_sticker/send`;
      // return yield put(imActions.sendValentineDaySticker(objectId));
    } else if (action === BalancePayAction.GIFT) {
      method = `/gifts/${extraId}/send`;
      params.toId = objectId;
      params.donateAmount = amount;
    } else if (action === BalancePayAction.APP_PURCHASE) {
      method = `/app/purchase/${objectId}/buy`;
    } else if (action === BalancePayAction.BUNDLE) {
      method = `/bundles/${objectId}/buy`;
    } else if (action === BalancePayAction.TRIAL_SUBSCRIPTION_ADULT) {
      method = `/pay/trial`;
      params.categoryId = 1;
      params.period = period;
      const targetId = getStorageItem('targetId');
      params.targetId = targetId ? parseInt(targetId, 10) || 0 : 0;
    } else if (action === BalancePayAction.QR_BONUS) {
      method = `/pay/qr_bonus`;
    } else {
      throw new Error('Unknown action');
    }

    const data = yield call(Api.post, method, params);

    const closeAction = closeSheet ? balancePayActions.close() : null;
    const toastSuccessAction = hideSuccessToast
      ? toastActions.hideToast()
      : toastActions.setToastSuccess();

    yield put(batchActions(closeAction, toastSuccessAction));

    if (
      action === BalancePayAction.TRIAL_SUBSCRIPTION_ADULT ||
      action === BalancePayAction.QR_BONUS
    ) {
      yield put(toastActions.hideToast());
      yield put(payActions.setInvoiceToken(data.token));
    } else if (action === BalancePayAction.MESSAGE) {
      const history = yield select(imHistorySelector, data.peerId);
      const message = history[data.messageId];
      if (message) {
        yield put(
          batchActions(
            imActions.setMessage({
              peerId: data.peerId,
              messageId: data.messageId,
              message: {
                ...message,
                isHidden: false,
                decryptKey: data.decryptKey,
              },
            }),
            toastActions.setToastSuccess(t('bought_message')),
          ),
        );
      }

      const asPeerIdBuy = yield select(imAsPeerIdSelector);
      yield put(
        imActions.loadChat({
          peerId: extraId,
          ...(asPeerIdBuy ? { asPeerId: asPeerIdBuy } : {}),
        }),
      );
      yield call(statReachGoal, 'buy_message');
    } else if (action === BalancePayAction.TIPS) {
      yield put(
        batchActions(toastActions.hideToast(), balanceActions.setIsTipsPaid(true)),
      );
      const asPeerIdTips = yield select(imAsPeerIdSelector);
      yield put(
        imActions.loadChat({
          peerId: objectId,
          ...(asPeerIdTips ? { asPeerId: asPeerIdTips } : {}),
        }),
      );
      yield call(statReachGoal, 'send_tips');
    } else if (action === BalancePayAction.BATTLE_DONATE) {
      yield put(balanceActions.setIsBattleDonatePaid(true));
      yield call(statReachGoal, 'battle_donate');
    } else if (action === BalancePayAction.SUBSCRIPTION_ADULT) {
      yield put(
        batchActions(
          mainActions.setDecryptKey({
            id: -1,
            decryptKey: data.decryptKey,
          }),
        ),
      );
      yield call(statReachGoal, 'subscription_adult_bought');
      goBack();
      yield call(statReachGoal, 'buy_adult_subscription');
    } else if (action === BalancePayAction.SUBSCRIPTION_MEDIA) {
      yield put(
        batchActions(
          mainActions.setDecryptKey({
            id: -2,
            decryptKey: data.decryptKey,
          }),
        ),
      );
    } else if (action === BalancePayAction.SUBSCRIPTION_PERSONAL) {
      yield put(
        batchActions(
          mainActions.setDecryptKey({
            id: objectId,
            decryptKey: data.decryptKey,
          }),
        ),
      );
      yield call(statReachGoal, 'buy_sponsor_subscription');
    } else if (action === BalancePayAction.ROULETTE) {
      yield put(
        batchActions(
          toastActions.hideToast(),
          rouletteActions.setResultsLotIndex({
            rouletteId: data.rouletteId,
            lotIndex: data.index,
          }),
          rouletteActions.setGameProgress(data.rouletteId),
          rouletteActions.setPrize(data.message),
          rouletteActions.clearLots(),
        ),
      );
      yield call(statReachGoal, 'buy_roulette_rotate');
    } else if (action === BalancePayAction.BUNDLE) {
      yield put(
        bundlesActions.setDecryptKey({
          bundleId: objectId,
          decryptKey: data.decryptKey,
        }),
      );
      yield put(toastActions.setToastSuccess());

      const user = yield select(currentUserSelector);
      yield put(adventCalendarActions.getAdventCalendar(user.id));
    } else if (action === BalancePayAction.VALENTINE) {
      yield put(
        batchActions(
          imActions.setMessage({
            peerId: objectId,
            messageId: data.message.id,
            message: data.message,
          }),
          toastActions.hideToast(),
        ),
      );
      yield call(goTo, CHAT, { peerId: objectId }, { replace: true });
      yield call(statReachGoal, 'valentine_day_sticker');
    } else if (action === BalancePayAction.GIFT) {
      yield put(
        batchActions(
          imActions.setMessage({
            peerId: objectId,
            messageId: data.message.id,
            message: data.message,
          }),
          toastActions.hideToast(),
        ),
      );
      yield call(goTo, CHAT, { peerId: objectId }, { replace: true });
      yield call(statReachGoal, 'gift_sent');
    } else if (action === BalancePayAction.APP_PURCHASE) {
      window.dispatchEvent(
        new CustomEvent('appPurchaseResult', {
          detail: { purchaseId: objectId, success: true },
        }),
      );
    }

    yield updateAllBalancesWorker();

    if (action !== BalancePayAction.TRIAL_SUBSCRIPTION_ADULT) {
      yield call(trackPurchase, +amount, action, `${objectId}_${extraId}`);
    }

    try {
      // @ts-ignore
      window.fbq('track', 'Lead');

      try {
        // @ts-ignore
        window.ttq.track('CompletePayment');
      } catch (e) {}
    } catch (e) {}
  } catch (e) {
    if (e.code === 'insufficient_funds') {
      yield put(
        batchActions(
          toastActions.hideToast(),
          balanceActions.setTopUpActive({
            topUpActive: true,
            requireTopUpAmount: a.payload.amount,
          }),
          balancePayActions.close(),
        ),
      );
    } else {
      yield put(toastActions.setToastFail(e.message));
    }
  }
}

function* getRatesWorker() {
  yield put(payActions.setLoadingRates(true));
  const eur = yield call(Api.post, '/rates?currency=eur');
  const ton = yield call(Api.post, '/rates?currency=ton');
  yield put(
    payActions.setRates({
      eur,
      ton,
    }),
  );
  yield put(payActions.setLoadingRates(false));
}

export function* updateAllBalancesWorker() {
  const {
    eurBalance,
    balance,
    placeBalance,
  }: { eurBalance: number; balance: number; placeBalance: number } = yield call(
    Api.get,
    '/balance',
  );

  yield put(
    batchActions(
      balanceActions.setBalance(balance.toString()),
      balanceActions.setBalance(eurBalance.toString()),
      mainActions.setBalance(balance),
      mainActions.setEurBalance(eurBalance),
      mainActions.setPlaceBalance(placeBalance),
    ),
  );
}

export function* topUpFromTonBalanceWorker(a: TopUpFromTonBalanceAction) {
  const amount = a.payload;
  yield put(toastActions.setToastLoading());
  try {
    const { action, objectId, extraId, isAnon } = (yield select(
      balancePaySelector,
    )) as BalancePayState;
    const { balance, eurBalance } = yield call(Api.post, '/balance/eur/top_up', {
      amount,
    });

    yield put(
      batchActions(
        toastActions.hideToast(),
        balanceActions.setBalance(balance.toString()),
        balanceActions.setBalance(eurBalance.toString()),
        mainActions.setBalance(balance),
        mainActions.setEurBalance(eurBalance),
      ),
    );

    if (action) {
      yield payWorker({
        type: a.type,
        payload: {
          action,
          objectId: objectId || 0,
          extraId,
          amount,
          isAnon,
          closeSheet: false,
          hideSuccessToast: true,
        },
      });
      yield put(balancePayActions.clearContext());
    }
  } catch (e) {
    console.log(e);
    if (e.code === 'insufficient_funds') {
      yield put(
        toastActions.setToastFail(t('top_up_balance_from_ton_insufficient_funds')),
      );
      yield put(
        balanceActions.setTopUpTonActive({
          topUpActive: true,
          requireTopUpAmount: e.params.amount,
        }),
      );
      return;
    }
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* attachCardWorker() {
  yield put(toastActions.setToastLoading());
  try {
    const { token: url } = yield call(Api.get, '/pay/attach_card', {
      params: {
        provider: PayProvider.tribute,
      },
    });
    yield put(toastActions.hideToast());
    if (!url) return;
    yield put(payActions.setInvoiceToken(url));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* balanceSaga() {
  yield all([
    takeLatest(balanceActions.loadBalance, loadBalanceWorker),
    takeLatest(balanceActions.generateInvoice, generateInvoiceWorker),
    takeLatest(balanceActions.withdraw, withdrawWorker),
    takeLatest(balanceActions.getAddress, getAddressWorker),
    takeLatest(balanceActions.topUp, topUpWorker),
    takeLatest(balanceActions.topUpRetry, topUpRetryWorker),
    takeLatest(balanceActions.loadTonRate, getRatesWorker),
    takeLatest(balanceActions.cryptoBayTopUp, cryptoBayTopUpWorker),
    takeLatest(balanceActions.pay, payWorker),
    takeLatest(balanceActions.updateAllBalances, updateAllBalancesWorker),
    takeLatest(balanceActions.topUpFromTonBalance, topUpFromTonBalanceWorker),
    takeLatest(balanceActions.attachCard, attachCardWorker),
  ]);
}
